import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore, useModelStore } from '@/store';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import { useTranslation } from '@/hooks/useTranslation';

function ModelDropdown({
  modelId,
  isSaved,
  onToggleSave,
  TriggerComponent,
  noRoot,
  isLongPress,
}) {
  const { openModal } = useModalStore(state => state.actions);
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const model = useModelStore(React.useCallback(state => state.getModelById(modelId), [modelId]));

  const handleViewDetails = useCallback(() => {
    openModal('ModelDetailsModal', { modelId });
  }, [openModal, modelId]);

  const content = (
    <>
      {TriggerComponent && (
        <DropdownMenu.Trigger action={isLongPress ? 'longPress' : undefined}>
          <TriggerComponent />
        </DropdownMenu.Trigger>
      )}

      <DropdownMenu.Content 
        className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
        align={isXS ? 'center' : 'end'} 
        sideOffset={isXS ? 4 : 5}
      >
        {Platform.OS === 'web' ? (
          // Web implementation using DropdownMenuItem
          <>
            {model?.name && (
              <>
                <DropdownMenuLabel key="model-title" text={model.name} />
                <DropdownMenuItemDivider key="model-title-divider" />
              </>
            )}
            {hasUser && (
              <DropdownMenuItem
                key="save"
                onSelect={onToggleSave}
                icon="star"
                title={isSaved ? t('dropdowns.unsave') : t('common.save')}
              />
            )}
            <DropdownMenuItem
              key="details"
              onSelect={handleViewDetails}
              icon="circle-info"
              title={t('dropdowns.seeModelDetails')}
            />
          </>
        ) : (
          // Native implementation using native components
          <>
            {model?.name && (
              <>
                <DropdownMenu.Item key="model-title" disabled>
                  <DropdownMenu.ItemTitle numberOfLines={1}>{model.name}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>
            )}
            {hasUser && (
              <DropdownMenu.Item key="save" onSelect={onToggleSave}>
                <DropdownMenu.ItemIcon ios={{ 
                  name: "star",
                  color: colors.text_color_primary 
                }} />
                <DropdownMenu.ItemTitle>
                  {isSaved ? t('dropdowns.unsave') : t('common.save')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item key="details" onSelect={handleViewDetails}>
              <DropdownMenu.ItemIcon ios={{ 
                name: "info.circle",
                color: colors.text_color_primary 
              }} />
              <DropdownMenu.ItemTitle>
                {t('dropdowns.seeModelDetails')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </>
  );

  if (noRoot) {
    return content;
  }

  return (
    <DropdownMenu.Root>
      {content}
    </DropdownMenu.Root>
  );
}

export default React.memo(ModelDropdown);
